import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { t } from '../../services/HelperService';
import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { getUser } from '../../services/UserService';
import {
  REPORT_DATE_FROM_TOKEN,
  REPORT_DATE_TO_TOKEN,
  ReportDateRangePicker,
  computeReportDateRange,
  usesReportDateRange,
} from './ReportDateRangePicker';
import { patchCustomizationPrefs, resolveInitialDateRange } from '../customizations/customizationUserPrefs';

const q_StartDataExport = queryNoTracking('InsyteWebApi_StartDataExport', 0, true);
const q_GetDataExportStatuses = queryNoTracking('InsyteWebApi_GetDataExportStatuses', 0, true);

/** Poll cadence for in-flight exports; every in-flight export shares one tick. */
const EXPORT_POLL_MS = 4000;

const safeString = (v) => (v ?? '').toString();

/** Rows shown per sheet in the preview table; the export itself has no such cap. */
const MAX_PREVIEW_TABLE_ROWS = 200;

/** Parses an output's ValueJson (the mapping script's evaluated value) to row objects. */
const parseMappedRows = (valueJson) => {
  const raw = safeString(valueJson).trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((r) => r && typeof r === 'object' && !Array.isArray(r)) : [];
  } catch {
    return [];
  }
};

/** Column headers in first-appearance order across all rows. */
const collectHeaders = (rows) => {
  const headers = [];
  const seen = new Set();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      headers.push(key);
    }
  }
  return headers;
};

/** Formats a cell for the preview table (the real export writes typed cells server-side). */
const formatCell = (value, locale) => {
  if (value == null) return '';
  if (typeof value === 'number') return value.toLocaleString(locale);
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  const text = safeString(value);
  // ISO date-time strings (the harness serializes dates as ISO) render as local dates.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString(locale);
  }
  return text;
};

/**
 * Builds the export definition from the session artifacts: one sheet per named query,
 * paired with the same-named output's mapping script (and, when set, the query's split
 * column — the sheet then fans out into one workbook tab per distinct value at
 * generate time). Returns { sheets, problems } — problems are pairing defects that
 * block the Generate button.
 */
export const buildExportSheets = (queries, outputs) => {
  const sheets = [];
  const problems = [];

  const namedQueries = (queries ?? []).filter((q) => safeString(q.name).trim());
  if (namedQueries.length === 0) {
    problems.push(t('Define at least one query.'));
    return { sheets, problems };
  }

  const outputByName = new Map((outputs ?? [])
    .filter((o) => safeString(o.name).trim())
    .map((o) => [safeString(o.name).trim().toLowerCase(), o]));

  for (const q of namedQueries) {
    const name = safeString(q.name).trim();
    if (!safeString(q.query).trim()) {
      problems.push(`${t('Sheet')} '${name}': ${t('the query is empty.')}`);
      continue;
    }
    const output = outputByName.get(name.toLowerCase());
    if (!output || !safeString(output.script).trim()) {
      problems.push(`${t('Sheet')} '${name}': ${t('no column mapping output with the same name — ask the assistant to add one.')}`);
      continue;
    }

    // A split column must actually exist among the mapped columns, or every exported
    // row would land on the "(blank)" tab — caught here, before Generate.
    const splitByColumn = safeString(q.splitByColumn).trim();
    if (splitByColumn) {
      const rows = parseMappedRows(output.valueJson);
      const headers = collectHeaders(rows);
      if (rows.length > 0 && !headers.some((h) => h.toLowerCase() === splitByColumn.toLowerCase())) {
        problems.push(`${t('Sheet')} '${name}': ${t('splits by column')} '${splitByColumn}', ${t('but the mapping produces no such column — ask the assistant to fix the split or the columns.')}`);
        continue;
      }
    }

    sheets.push({
      name,
      query: safeString(q.query),
      script: safeString(output.script),
      splitByColumn: splitByColumn || undefined,
    });
  }
  return { sheets, problems };
};

/** Sheets named in the label before collapsing to "+ N more" — a many-sheet label must stay one readable line. */
const MAX_LABEL_SHEETS = 3;

/** Hard cap on the label, matching the server's notification-subject cap. */
const MAX_LABEL_LENGTH = 255;

const truncateLabel = (text) => (text.length <= MAX_LABEL_LENGTH ? text : `${text.slice(0, MAX_LABEL_LENGTH - 1)}…`);

/**
 * Composes the human description of one export snapshot: each sheet with the artifact
 * versions it captured, plus the date window when one is in play — e.g.
 * "data (q v3, cols v2), 1/06/2026 – 30/06/2026". Returns:
 * - label: one capped line (first few sheets + "+ N more") — rides in the export
 *   definition (→ notification subject), so the dialog's exports panel and the bell
 *   tell the same story;
 * - detail: every sheet on its own line, for the panel row's tooltip.
 */
export const composeExportLabel = (sheets, versions, range, locale) => {
  const parts = (sheets ?? []).map((s) => {
    const v = versions?.[safeString(s.name).trim().toLowerCase()] ?? {};
    const bits = [];
    if (v.queryVersion != null) bits.push(`q v${v.queryVersion}`);
    if (v.mappingVersion != null) bits.push(`cols v${v.mappingVersion}`);
    return bits.length > 0 ? `${s.name} (${bits.join(', ')})` : safeString(s.name);
  });
  const window = range ? `${range.from.toLocaleDateString(locale)} – ${range.to.toLocaleDateString(locale)}` : '';

  const shown = parts.slice(0, MAX_LABEL_SHEETS);
  const moreCount = parts.length - shown.length;
  let label = shown.join(', ') + (moreCount > 0 ? ` + ${moreCount} ${t('more')}` : '');
  if (window) label += `, ${window}`;

  const detail = parts.join('\n') + (window ? `\n${window}` : '');
  return { label: truncateLabel(label), detail };
};

/**
 * The data-export profile's preview: a tab strip mirroring the WORKBOOK's worksheet
 * tabs (a split sheet appears as its per-value tabs, other sheets as their name tab),
 * each showing its mapped rows as a table with a bold header — a faithful sample of
 * the Excel file the export will produce — plus the "Generate Export" button.
 *
 * The button is deliberately DISCONNECTED from the AI: it snapshots the current session
 * artifacts (sheets + template values) and posts them to the start-export endpoint,
 * which re-validates the definition and queues a background task. Every export started
 * from this dialog is tracked in the "Exports" panel — a shared poll follows the ones
 * in flight and swaps in the download link (or failure) in place, so the user never has
 * to leave the dialog; the notification bell carries the same entries (matching labels)
 * for after the dialog closes.
 *
 * When any query uses the {{report.dateFrom}}/{{report.dateTo}} tokens a date-range
 * bar appears; the chosen instants feed the preview refresh AND ride along in the
 * export definition's template values.
 */
export const DataExportPreview = ({ queries, outputs, busy, apiVersion, sessionOptions, templateValues, onTemplateValuesChange, onSessionOptionsChange, onRefresh, onDefect, getArtifactVersions, designMode = true, stateKey }) => {
  const user = getUser();
  const locale = safeString(user?.Locale) || 'en-AU';
  const timeZone = safeString(user?.IANATimeZoneID) || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const namedQueries = (queries ?? []).filter((q) => safeString(q.name).trim());

  const outputFor = (name) => (outputs ?? [])
    .find((o) => safeString(o.name).trim().toLowerCase() === safeString(name).trim().toLowerCase());
  const sampleBasedFor = (q) => !!(q?.lastResult?.SampleRowsUsedForTests ?? q?.lastResult?.sampleRowsUsedForTests);

  // ── Preview tabs: the strip mirrors the WORKBOOK's worksheet tabs ──
  // A split sheet has NO tab of its own in the generated file — it fans out entirely
  // into one worksheet tab per distinct value of its split column. So the preview
  // strip shows those value tabs (over the preview slice) in place of the sheet-name
  // tab, exactly like Excel will. Sheets without a split keep their name tab. A
  // sample-based sheet (query matched no rows) contributes no value tabs — values
  // fabricated from sample rows read as real data — and falls back to its name tab
  // with the sample banner.
  const previewTabs = [];
  for (const q of namedQueries) {
    const name = safeString(q.name);
    const split = safeString(q.splitByColumn).trim();
    const rows = split && !sampleBasedFor(q) ? parseMappedRows(outputFor(name)?.valueJson) : [];
    const header = split ? collectHeaders(rows).find((h) => h.toLowerCase() === split.toLowerCase()) : undefined;
    let fannedOut = false;
    if (header) {
      const seen = new Set();
      for (const row of rows) {
        const value = safeString(row[header]).trim() || t('(blank)');
        if (seen.has(value.toLowerCase())) continue;
        seen.add(value.toLowerCase());
        previewTabs.push({
          key: `${name}::${value}`,
          queryName: name,
          splitValue: value,
          label: value,
          title: `${t('Workbook tab')} '${value}' — ${t('sheet')} '${name}' ${t('split by')} '${header}'`,
        });
      }
      fannedOut = seen.size > 0;
    }
    if (!fannedOut) {
      previewTabs.push({ key: name, queryName: name, splitValue: undefined, label: name, title: name });
    }
  }

  // ── Active tab (falls back to the first when the selected one disappears) ──
  const [activeTabKey, setActiveTabKey] = React.useState('');
  const activeTab = previewTabs.find((tab) => tab.key === activeTabKey) ?? previewTabs[0];
  const effectiveActive = safeString(activeTab?.queryName);

  // ── Viewer-controlled date range (same contract as the ad-hoc report preview) ──
  // Seeded from the viewer's remembered choice, then the author's saved default.
  const [dateRange, setDateRange] = React.useState(() => resolveInitialDateRange(stateKey, sessionOptions));
  const showDateRange = usesReportDateRange(queries);
  const computedRange = React.useMemo(
    () => (showDateRange
      ? computeReportDateRange(dateRange.preset, timeZone, dateRange.customFrom, dateRange.customTo)
      : null),
    [showDateRange, dateRange, timeZone]
  );

  // First time the queries start using the date tokens, seed the session's template
  // values with the default preset so refreshes and the export run the window shown.
  // Live mode always seeds: the definition carries the AUTHOR's instants from save
  // time, and the run must match the (possibly restored) range the bar displays.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (!showDateRange || seededRef.current) return;
    seededRef.current = true;
    if (!computedRange) return;
    const hasValues = !!safeString(templateValues?.[REPORT_DATE_FROM_TOKEN]).trim() &&
      !!safeString(templateValues?.[REPORT_DATE_TO_TOKEN]).trim();
    if (designMode && hasValues) return;
    onTemplateValuesChange?.({
      [REPORT_DATE_FROM_TOKEN]: computedRange.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: computedRange.to.toISOString(),
    });
    // Seeding is a one-shot on first token appearance, not a value-tracking effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDateRange]);

  const onDateRangeChange = (next) => {
    setDateRange(next);
    // Design mode: the author's last selection becomes the customization's default
    // (preset only). Live mode: the viewer's own selection is remembered.
    if (designMode) onSessionOptionsChange?.({ defaultDateRange: safeString(next.preset) });
    else if (stateKey) patchCustomizationPrefs(stateKey, { dateRange: next });
    const range = computeReportDateRange(next.preset, timeZone, next.customFrom, next.customTo);
    if (!range) return; // incomplete custom range — wait for both dates
    onRefresh?.({
      [REPORT_DATE_FROM_TOKEN]: range.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: range.to.toISOString(),
    });
  };

  // ── Generate Export ──
  const { sheets, problems } = buildExportSheets(queries, outputs);

  // Every export started from this dialog, newest first. Each entry is the visible
  // trace of one snapshot: { notificationId, startedAt, label, status:
  // 'inProgress'|'done'|'failed', url, fileName, error }. The button stays enabled
  // while earlier exports run — 'starting' only covers the POST itself.
  const [exportEntries, setExportEntries] = React.useState([]);
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState('');

  const onGenerateExport = async (e) => {
    try {
      setStarting(true);
      setStartError('');
      const { label, detail } = composeExportLabel(sheets, getArtifactVersions?.() ?? {}, computedRange, locale);
      const res = throwIfFrameworkException(await q_StartDataExport.getFirstOrDefault({
        apiVersion: safeString(apiVersion) || 'v2',
        // Sheet queries are SQL (the dataExport profile's dialect); the background
        // task replays this snapshot through the SQL executor.
        queryLanguage: 'sql',
        label,
        sheets,
        templateValues: { ...(templateValues ?? {}) },
      }));
      const ok = !!(res?.Ok ?? res?.ok);
      if (ok) {
        setExportEntries((prev) => [{
          notificationId: res?.NotificationID ?? res?.notificationID,
          startedAt: new Date(),
          label,
          detail,
          status: 'inProgress',
          url: '',
          fileName: '',
          error: '',
        }, ...prev]);
      } else {
        const error = safeString(res?.Error ?? res?.error) || t('The export could not be started.');
        setStartError(error);
        onDefect?.(error);
      }
    } catch (err) {
      const error = safeString(err?.message) || safeString(err);
      setStartError(error);
      onDefect?.(error);
    } finally {
      setStarting(false);
      e?.promise?.();
    }
  };

  // ── Shared status poll — one round trip per tick for ALL in-flight exports ──
  const inProgressKey = exportEntries
    .filter((entry) => entry.status === 'inProgress')
    .map((entry) => entry.notificationId)
    .join(',');

  React.useEffect(() => {
    if (!inProgressKey) return undefined;
    const ids = inProgressKey.split(',').map(Number);
    let cancelled = false;

    const tick = async () => {
      let statuses;
      try {
        statuses = await q_GetDataExportStatuses.getAll({ notificationIDs: ids });
      } catch {
        return; // transient poll failure — the next tick retries
      }
      if (cancelled || !Array.isArray(statuses)) return;

      const byId = new Map(statuses.map((s) => [s.NotificationID ?? s.notificationID, s]));
      setExportEntries((prev) => prev.map((entry) => {
        if (entry.status !== 'inProgress') return entry;
        const status = byId.get(entry.notificationId);
        const statusText = safeString(status?.Status ?? status?.status);
        if (statusText === 'Done') {
          return {
            ...entry,
            status: 'done',
            url: safeString(status.Url ?? status.url),
            fileName: safeString(status.FileName ?? status.fileName) || 'data_export.xlsx',
          };
        }
        if (statusText === 'Failed') {
          return {
            ...entry,
            status: 'failed',
            error: safeString(status.Error ?? status.error) || t('Export failed please try again'),
          };
        }
        return entry;
      }));

      // Keep the bell in step the moment something finishes (it shows the same entries).
      const finished = statuses.some((s) => {
        const statusText = safeString(s.Status ?? s.status);
        return statusText === 'Done' || statusText === 'Failed';
      });
      if (finished) window.accentNotificationsRefresh?.(true);
    };

    tick();
    const interval = setInterval(tick, EXPORT_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [inProgressKey]);

  // ── Active sheet table data ──
  const activeQuery = namedQueries.find((q) => q.name === effectiveActive);
  const activeOutput = outputFor(effectiveActive);
  const mappedRows = parseMappedRows(activeOutput?.valueJson);
  const headers = collectHeaders(mappedRows);

  // When the AI turn's query returned no rows, the mapping was tested against entity
  // SAMPLE rows instead — so the mapped rows on screen are real field names but NOT the
  // export's data, and anything derived from their values (like split tabs) is fake.
  const sampleBased = sampleBasedFor(activeQuery);

  // Sheets whose latest run matched no rows: generating now writes them as empty tabs.
  // Surfaced as a standing (non-blocking) warning so nobody exports a blank workbook
  // without noticing — an empty sheet can still be a legitimate result.
  const zeroRowSheets = namedQueries
    .filter((q) => safeString(q.query).trim())
    .filter((q) => (q.lastResult?.ValueCount ?? q.lastResult?.valueCount) === 0)
    .map((q) => safeString(q.name));

  // ── Split filtering: when the active tab is a fanned-out value tab, the table
  // shows only that value's rows — the same rows Excel puts on that worksheet. ──
  const activeSplit = safeString(activeQuery?.splitByColumn).trim();
  const splitHeader = activeSplit && !sampleBased
    ? headers.find((h) => h.toLowerCase() === activeSplit.toLowerCase())
    : undefined;
  const splitValueOf = (row) => safeString(splitHeader ? row[splitHeader] : '').trim() || t('(blank)');
  const effectiveSplitValue = safeString(activeTab?.splitValue);
  const splitRows = splitHeader && effectiveSplitValue
    ? mappedRows.filter((row) => splitValueOf(row) === effectiveSplitValue)
    : mappedRows;

  const visibleRows = splitRows.slice(0, MAX_PREVIEW_TABLE_ROWS);
  const fetchedCount = activeQuery?.lastResult?.ValueCount ?? activeQuery?.lastResult?.valueCount;
  const totalCount = activeQuery?.lastResult?.TotalCount ?? activeQuery?.lastResult?.totalCount;

  // $top is SEMANTIC for exports (the sheet's row limit, honored by the generated
  // file); the footer states this sheet's real export behavior so the preview slice
  // is never mistaken for it. Previews are clamped server-side, not via $top.
  const sheetTopMatch = /[?&]\$top=(\d+)/i.exec(safeString(activeQuery?.query));
  const sheetRowLimit = sheetTopMatch ? Number(sheetTopMatch[1]) : null;

  const tabStyle = (selected) => ({
    border: '1px solid #dee2e6',
    borderBottom: selected ? '1px solid #fff' : '1px solid #dee2e6',
    borderRadius: '6px 6px 0 0',
    marginBottom: -1,
    background: selected ? '#fff' : '#f8f9fa',
    padding: '5px 14px',
    fontSize: 12,
    fontWeight: selected ? 700 : 500,
    color: selected ? '#6f42c1' : '#495057',
    cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <AccentButton outline size="sm" disabled={busy} onClick={(e) => { onRefresh?.(); e?.promise?.(); }}>
          {t('Refresh preview')}
        </AccentButton>
        {showDateRange && (
          <ReportDateRangePicker
            value={dateRange}
            disabled={busy}
            timeZone={timeZone}
            locale={locale}
            onChange={onDateRangeChange}
          />
        )}
        <div style={{ flex: 1 }} />
        <AccentButton
          tagName="generateExport"
          disabled={busy || starting || problems.length > 0 || sheets.length === 0}
          onClick={onGenerateExport}
        >
          {starting ? t('Starting...') : t('Generate Export')}
        </AccentButton>
      </div>

      {problems.length > 0 && (
        <div style={{ border: '1px solid #ffe0b2', background: '#fff8e1', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
          {problems.map((p) => <div key={p}>{p}</div>)}
        </div>
      )}
      {zeroRowSheets.length > 0 && (
        <div data-tagname="zeroRowWarning" style={{ border: '1px solid #ffe0b2', background: '#fff8e1', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
          {zeroRowSheets.map((name) => (
            <div key={name}>
              {t('Sheet')} '{name}': {t('the query matched NO rows with the current filters/date range — generating now writes it as an empty tab. Change the date range above or ask the assistant to adjust the filters.')}
            </div>
          ))}
        </div>
      )}
      {startError && (
        <div style={{ border: '1px solid #ffcdd2', background: '#ffebee', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#b71c1c' }}>
          {startError}
        </div>
      )}

      {/* ── Exports this session: live status + in-place download links ── */}
      {exportEntries.length > 0 && (
        <div data-tagname="exportsPanel" style={{ border: '1px solid #dee2e6', background: '#f8f9fa', borderRadius: 6, padding: '6px 10px', display: 'grid', gap: 4 }}>
          {exportEntries.map((entry, ix) => {
            // Only the newest export reads at full strength; superseded ones fade
            // back — unless still generating, which deserves attention at any age.
            const emphasized = ix === 0 || entry.status === 'inProgress';
            return (
            <div key={entry.notificationId} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12, minWidth: 0, opacity: emphasized ? 1 : 0.55 }}>
              <span style={{ opacity: 0.65, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {entry.startedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: ix === 0 ? 600 : 400 }} title={entry.detail || entry.label}>
                {entry.label}
              </span>
              {entry.status === 'inProgress' && (
                <span style={{ color: '#6f42c1', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>{t('Generating')}&hellip;</span>
              )}
              {entry.status === 'done' && (
                <a href={entry.url} target="_blank" rel="noreferrer" download={entry.fileName} style={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {t('Download')}
                </a>
              )}
              {entry.status === 'failed' && (
                <span style={{ color: '#b71c1c', whiteSpace: 'nowrap', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '50%' }} title={entry.error}>
                  {t('Failed')}: {entry.error}
                </span>
              )}
            </div>
            );
          })}
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {t('Exports also appear in the notification bell — you can close this dialog while one runs.')}
          </div>
        </div>
      )}

      {/* ── Worksheet tabs: exactly the tabs the generated workbook will have — a
             split sheet appears as its per-value tabs, never as a sheet-name tab ── */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid #dee2e6' }}>
        {previewTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            data-tagname={tab.splitValue !== undefined ? `splitValueTab_${tab.queryName}_${tab.splitValue}` : `sheetTab_${tab.queryName}`}
            title={tab.title}
            style={tabStyle(tab.key === activeTab?.key)}
            onClick={() => setActiveTabKey(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Sample-data banner: the rows below are NOT the export's data ── */}
      {sampleBased && headers.length > 0 && (
        <div data-tagname="samplePreviewBanner" style={{ border: '1px solid #ffe0b2', background: '#fff8e1', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600 }}>
          {t('The query matched no rows, so the rows below are mapped SAMPLE rows from the entity — real column names, NOT your export\u2019s data.')}
          {activeSplit && ` ${t('The per-value tabs can\u2019t be previewed until the query matches real rows.')}`}
        </div>
      )}

      {/* ── Active sheet table ── */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, border: '1px solid #dee2e6', borderTop: sampleBased && headers.length > 0 ? '1px solid #dee2e6' : 'none', borderRadius: '0 0 6px 6px', overflow: 'auto', background: '#fff' }}>
        {headers.length > 0 ? (
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h} style={{ position: 'sticky', top: 0, background: '#f8f9fa', borderBottom: '2px solid #dee2e6', padding: '6px 10px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, ix) => (
                // Preview rows have no identity; index keys are fine for a read-only slice.
                // eslint-disable-next-line react/no-array-index-key
                <tr key={ix}>
                  {headers.map((h) => (
                    <td key={h} style={{ borderBottom: '1px solid #f1f3f5', padding: '4px 10px', whiteSpace: 'nowrap' }}>
                      {formatCell(row[h], locale)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
            {fetchedCount === 0
              // A refresh re-maps the REAL rows, so a 0-row query leaves the table
              // honestly empty — not "no preview yet", the preview ran and found nothing.
              ? t('The query matched no rows with the current filters/date range — change the date range above or ask the assistant to adjust the filters.')
              : t('No preview yet — describe the export you want in the conversation (e.g. "all open jobs with customer, sales rep and amount"), then check each sheet\u2019s columns here.')}
          </div>
        )}
        {busy && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}>
            <AccentSpinner />
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, opacity: 0.65 }}>
        {headers.length > 0 && (
          <span>
            {sampleBased ? t('SAMPLE preview (not your export\u2019s data)') : t('Preview')}: {visibleRows.length < splitRows.length
              ? `${t('first')} ${visibleRows.length} ${t('of')} ${splitRows.length} ${t('rows')}`
              : `${splitRows.length} ${t('rows')}`}
            {splitHeader && effectiveSplitValue && ` (${t('workbook tab')} '${effectiveSplitValue}')`}
            {fetchedCount != null && ` (${t('from a preview slice of')} ${fetchedCount}${totalCount != null ? ` ${t('of')} ${totalCount} ${t('matching')}` : ''} ${t('rows')})`}
            {'. '}
          </span>
        )}
        {activeSplit && `${t('Sheet')} '${effectiveActive}' ${t('splits into one workbook tab per distinct')} '${activeSplit}' ${t('value — the tabs above only cover the preview slice; the generated file fans out over ALL matching rows.')} `}
        {sheetRowLimit != null
          ? `${t('This sheet exports at most')} ${sheetRowLimit.toLocaleString(locale)} ${t('rows ($top is the sheet\u2019s row limit).')}`
          : t('This sheet exports EVERY matching row — the preview only shows a slice.')}
      </div>
    </div>
  );
};
